import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {BehaviorSubject, Subscription} from 'rxjs';
import {
    IPackage_ProductAllocation,
    IPackagesEvent,
    IProduct_ProductAllocation,
    PackingTypeNew,
    UnitsOfMeasure
} from '../../product-allocation-new.component';

export function moreThen(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        return parseFloat(control.value) > 0 ? null : {wrongValue: true};
    };
}

@Component({
    selector: 'eci-pack-items',
    templateUrl: './pack-items.component.html',
    styleUrls: ['./pack-items.component.scss']
})
export class PackItemsComponent implements OnInit, OnDestroy {

    @Input() set packagesEvents(event: BehaviorSubject<IPackagesEvent>) {
        if (event) {
            this.event = event;
            this.packagesSubscription = this.event.subscribe(event => {
                this.eventsDispatcher(event);
            });
        }
    }
    @Input() isSimply: boolean = false;
    @Input() notAllocated: number = 0;
    @Input() denominator: number = 1;
    @Input() products: IProduct_ProductAllocation[] = [];
    @Input() packages: IPackage_ProductAllocation[] = [];
    @Input() isViewing: boolean = false;
    @Input() listOfSelectedIndexes: number[] = [];
    @Output() listOfSelectedIndexesChange = new EventEmitter<number[]>();
    @Output() packagesChange: EventEmitter<{
        pack: IPackage_ProductAllocation,
        index: number
    }> = new EventEmitter<{
        pack: IPackage_ProductAllocation,
        index: number
    }>();
    @Output() onQuantityChange: EventEmitter<any> = new EventEmitter<any>();

    packagesSubscription: Subscription;
    event: BehaviorSubject<IPackagesEvent>;

    lastPalletIndex = 0;
    lastCartonIndex = 0;

    constructor() {
    }

    ngOnInit(): void {
        if (this.packages.length) {
            const single_cartons = this.packages.filter(p => p.packingType === PackingTypeNew.single_carton);
            if (single_cartons.length) {
                this.lastCartonIndex = Math.max(...single_cartons.map(p => p.index || 0));
            }
            const pallets = this.packages.filter(p => p.packingType === PackingTypeNew.pallet);
            if (pallets.length) {
                this.lastPalletIndex = Math.max(...pallets.map(p => p.index || 0));
            }
        }
    }

    ngOnDestroy(): void {
        this.packagesSubscription && this.packagesSubscription.unsubscribe();
    }

    eventsDispatcher(event: IPackagesEvent) {
        if (event) {
            switch (event.event) {
                case 'new_pallet':
                    this.addNewPallet();
                    break;
                case 'new_carton':
                    this.addNewCarton();
                    break;
            }
        }
    }

    onCancelPack(event: IPackage_ProductAllocation, index: number) {
        this.packages.splice(index, 1);
        this.listOfSelectedIndexes = this.listOfSelectedIndexes
            .filter(i => i !== index)
            .map(i => i > index ? i - 1 : i);
        this.listOfSelectedIndexesChange.emit(this.listOfSelectedIndexes);
        this.reindexPackages();
        this.packagesChange.emit({
            pack: event,
            index
        });
    }

    isChecked(index: number): boolean {
        return this.listOfSelectedIndexes.includes(index);
    }

    selectionChanged(event: MatCheckboxChange, index: number): void {
        if (event.checked) {
            if (!this.isChecked(index)) {
                this.listOfSelectedIndexes = [...this.listOfSelectedIndexes, index];
            }
        } else {
            this.listOfSelectedIndexes = this.listOfSelectedIndexes.filter(i => i !== index);
        }
        this.listOfSelectedIndexesChange.emit(this.listOfSelectedIndexes);
    }

    deleteSelected(): void {
        if (!this.listOfSelectedIndexes.length) {
            return;
        }
        [...this.listOfSelectedIndexes].sort((a, b) => b - a).forEach(index => this.packages.splice(index, 1));
        this.listOfSelectedIndexes = [];
        this.listOfSelectedIndexesChange.emit(this.listOfSelectedIndexes);
        this.reindexPackages();
        this.packagesChange.emit({pack: null, index: -1});
    }

    private reindexPackages() {
        this.lastPalletIndex = 0;
        this.lastCartonIndex = 0;
        this.packages.forEach(pl => {
            if (pl.packingType === PackingTypeNew.pallet) {
                pl.index = ++this.lastPalletIndex;
            } else {
                pl.index = ++this.lastCartonIndex;
            }
        });
    }

    private addNewPallet() {
        this.packages.push({
            packingType: PackingTypeNew.pallet,
            productsQuantity: 0,
            length: 0,
            width: 0,
            height: 0,
            cartons_qty: 0,
            unitsOfMeasure: UnitsOfMeasure.Kg,
            grossWeight: 0,
            volumetricWeight: 0,
            expand: true,
            cartons: [],
            index: ++this.lastPalletIndex,
            isNew: true,
        });
    }

    public addNewCarton() {
        this.packages.push({
            packingType: PackingTypeNew.single_carton,
            productsQuantity: 0,
            length: 0,
            width: 0,
            height: 0,
            cartons_qty: 0,
            unitsOfMeasure: UnitsOfMeasure.Kg,
            grossWeight: 0,
            volumetricWeight: 0,
            expand: true,
            products: [
                {item: {}, allocated: 0}
            ],
            index: ++this.lastCartonIndex,
            isNew: true,
        })
    }

    onPackChange(e: IPackage_ProductAllocation, index: number) {
        this.packagesChange.emit({
            pack: e,
            index
        });
    }

    onDuplicate(e: { package: IPackage_ProductAllocation, count: number }) {
        const pkg = e.package;
        switch (pkg.packingType) {
            case PackingTypeNew.pallet:
                for (let i = 0; i < e.count; i++) {
                    this.packages.push({
                        packingType: PackingTypeNew.pallet,
                        productsQuantity: pkg.productsQuantity,
                        length: pkg.length,
                        width: pkg.width,
                        height: pkg.height,
                        cartons_qty: pkg.cartons_qty || 0,
                        unitsOfMeasure: pkg.unitsOfMeasure || UnitsOfMeasure.Kg,
                        grossWeight: pkg.grossWeight,
                        volumetricWeight: pkg.volumetricWeight,
                        expand: false,
                        isNew: false,
                        cartons: (pkg.cartons || []).map(c => ({
                            packingType: PackingTypeNew.carton,
                            productsQuantity: c.productsQuantity,
                            length: c.length,
                            width: c.width,
                            height: c.height,
                            cartons_qty: c.cartons_qty || 0,
                            unitsOfMeasure: c.unitsOfMeasure || UnitsOfMeasure.Kg,
                            grossWeight: c.grossWeight,
                            volumetricWeight: c.volumetricWeight,
                            expand: false,
                            isNew: false,
                            products: (c.products || []).map(p => ({
                                item: p.item ? {...p.item} : p.item,
                                allocated: p.allocated
                            })),
                            index: c.index,
                        })),
                        index: ++this.lastPalletIndex,
                    });
                }
                this.packagesChange.emit({
                    pack: this.packages[this.packages.length - 1],
                    index: this.packages.length - 1
                });
                break;
            case PackingTypeNew.single_carton:
                for (let i = 0; i < e.count; i++) {
                    this.packages.push({
                        packingType: PackingTypeNew.single_carton,
                        productsQuantity: pkg.productsQuantity,
                        length: pkg.length,
                        width: pkg.width,
                        height: pkg.height,
                        cartons_qty: pkg.cartons_qty || 0,
                        unitsOfMeasure: pkg.unitsOfMeasure || UnitsOfMeasure.Kg,
                        grossWeight: pkg.grossWeight,
                        volumetricWeight: pkg.volumetricWeight,
                        expand: false,
                        isNew: false,
                        products: pkg.products.map(p => ({
                            item: p.item ? {...p.item} : p.item,
                            allocated: p.allocated
                        })),
                        index: ++this.lastCartonIndex,
                    });
                }
                this.packagesChange.emit({
                    pack: this.packages[this.packages.length - 1],
                    index: this.packages.length - 1
                });
                break;
        }
    }
}
